/* NB! Not to be included in services.ts */

/* Handles logging in/out */
import { Inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpInterceptorService } from '../http-interceptor/http-interceptor.service';

import { Router } from '@angular/router';
import { StyleService } from '../style/style.service';
import { TokenService } from './token/token.service';
import { GlobalFunctions } from '../global-functions/global-functions.service';
import { ClearService } from '../clear/clear.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TermsAndConditionsComponent } from 'src/app/components/terms-and-conditions/terms-and-conditions.component';
import { UserService } from 'src/app/lib';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../dialog/dialog.service';
import { WhitelabelInstance } from '../whitelabel/whitelabel-instance.service';
import { MenuService } from '../menu/menu.service';
import { APP_CONFIG, AppConfig, Menus, SMSAuthenticationRequest } from '@smartcharge/shared/index';

export enum SSOProvider {
  Microsoft = 'Microsoft'
}

const helper = new JwtHelperService();
@Injectable()
export class AuthService {
  redirectTo: string;
  constructor(
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    private _http: HttpInterceptorService,
    private router: Router,
    private userService: UserService,
    private styleService: StyleService,
    private tokenService: TokenService,
    private gf: GlobalFunctions,
    //private jwtHelper: JwtHelper,
    private clearService: ClearService,
    private dialog: MatDialog,
    private menuService: MenuService,
    private whiteLabelInstance: WhitelabelInstance,
    private translate: TranslateService,
    private dialogService: DialogService
  ) {}

  login(user, dashboard = true): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!user) {
        reject(this.translate.instant('PROVIDE_USERNAME_AND_PASSWORD'));
      } else if (!user.Username) {
        reject(this.translate.instant('PROVIDE_USERNAME'));
      } else if (!user.Password) {
        reject(this.translate.instant('PROVIDE_PASSWORD'));
      } else {
        let appID = this.gf.guid4();
        user = {
          Email: user.Username.toLowerCase(),
          Password: user.Password,
          isDashboard: dashboard,
          appID: appID,
          appToken: this.whiteLabelInstance.whitelabelID,
          TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        this._http.postUnsecure('Users/Authenticate', user).subscribe({
          next: async (res) => {
            if (res && res.accessToken && res.rToken && res.customer && res.user) {
              if (res.Menus) {
                this.menuService.refreshMenus(res.Menus);
              }

              if (res.MustConfirmCompanyInformation) {
                this.redirectTo = '/billing';
              }

              await this.setupUser(res, appID);

              resolve(res);
            } else if (res === 'wrongUsernameOrPassword') {
              reject(this.translate.instant('WRONG_USERNAME_OR_PASSWORD'));
            } else {
              reject(this.translate.instant('UNAUTHORIZED'));
            }
          },
          error: (err) => {
            if (err && err.errorName && err.errorName.toUpperCase() === 'UNAUTHORIZED') {
              reject({
                reason: this.translate.instant('NO_PORTAL_PERMISSION'),
                extra: this.translate.instant('NO_PERMISSION_EXTRA_HELP')
              });
            } else {
              reject(this.translate.instant('SOMETHING_WENT_WRONG'));
            }
          }
        });
      }
    });
  }

  loginWithMicrosoft() {
    const redirectUrl = this.getSSORedirectUrl();

    window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=2d114f46-05b0-4903-81b7-1fb2a3e56574&response_type=code&response_mode=query&redirect_uri=${encodeURIComponent(redirectUrl)}&scope=https%3A%2F%2Fgraph.microsoft.com%2Fuser.read%20&state=12345`;
  }

  loginSSO(code: string, provider: SSOProvider) {
    const redirectUrl = this.getSSORedirectUrl();

    const appID = this.gf.guid4();

    const payload = {
      AppToken: this.whiteLabelInstance.whitelabelID,
      AppId: appID,
      IsDashboard: true,
      Code: code,
      Provider: provider,
      Redirect: redirectUrl
    };

    this._http.postUnsecure('Users/sso/login', payload).subscribe(async (response) => {
      await this.setupUser(response, appID);

      this.router.navigateByUrl('/');
    });
  }

  smsAuth(payload: SMSAuthenticationRequest): Promise<any> {
    return new Promise((resolve, reject) => {
      const appid = this.gf.guid4();

      const data = {
        appID: appid,
        PhoneNumber: payload.Phone,
        CountryIndicatorID: payload.CountryID,
        OneTimePassword: payload.Code,
        appToken: this.appConfig.appToken,
        isDashboard: true
      };

      this._http.postUnsecure('Users/two-factor-auth/login/sms', data).subscribe(
        async (res) => {
          if (res.DoesUserNeedToCompleteRegistration) {
            resolve(res);
          } else if (res.user && res.customer) {
            await this.setupUser(res, appid);

            this.router.navigateByUrl('/');
          } else {
            reject(res);
          }
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  async logout(redirect?) {
    const seenChangeLogs = localStorage.getItem('HW_seenItems[yZmo1J]');
    const readChangeLogs = localStorage.getItem('HW_readItems[yZmo1J]');
    this.userService.clearUser();
    this.styleService.clearStyle();
    this.tokenService.clearToken();
    this.menuService.clearMenu();
    this.clearService.clearAll();
    this.redirectTo = null;

    if (redirect) {
      let env = localStorage.getItem('environment');
      localStorage.clear();
      if (seenChangeLogs) localStorage.setItem('HW_seenItems[yZmo1J]', seenChangeLogs);
      if (readChangeLogs) localStorage.setItem('HW_readItems[yZmo1J]', readChangeLogs);
      if (env === 'staging' || env === 'debug' || env === 'sandbox') {
        localStorage.setItem('environment', env);
      }
      this.router.navigate(['auth/login']);
      //location.href = "/#/auth/login"
      //location.replace('/#/auth/login');
    }
  }

  getRoles() {
    return this._http.get(`UserRoles/RolesOfAUser`);
  }

  refreshMenus() {
    this._http.get<Menus>('users/menus').subscribe({
      next: (menus) => {
        this.menuService.refreshMenus(menus);
      }
    });
  }

  private getSSORedirectUrl(): string {
    return `${window.location.origin}/auth/sso`;
  }

  private init(loginObject, appID) {
    //Setting style
    this.styleService.init(loginObject?.DashboardPreferences);

    this.userService.storeUser(
      loginObject.customer,
      loginObject.accessToken,
      loginObject.rToken,
      appID,
      loginObject.user.FK_UserTypeID,
      loginObject.Roles,
      loginObject.CompaniesUsersHasAccessTo,
      loginObject.OperatorsUserHasAccessTo,
      loginObject.InstallersUserHasAccessTo,
      loginObject.userPreferences
    );
  }

  private setupUser(loginObject, appID) {
    return new Promise((resolve) => {
      this.init(loginObject, appID);

      if (loginObject.TermsAndConditionToAcceptForAStationOwner) {
        let dialogRef: MatDialogRef<TermsAndConditionsComponent>;

        dialogRef = this.dialog.open(TermsAndConditionsComponent, {
          disableClose: true,
          autoFocus: false
        });
        dialogRef.componentInstance.termsURL =
          'https://f.hubspotusercontent40.net/hubfs/2283248/Content/SmartCharge_Generelle_betingelser_v2021.06.pdf'; //res.TermsAndConditionToAcceptForAStationOwner.en || res.TermsAndConditionToAcceptForAStationOwner.nb;///res.TermsAndConditionToAcceptForAStationOwner.en || res.TermsAndConditionToAcceptForAStationOwner.nb;
        dialogRef.componentInstance.userID = loginObject.user.PK_UserID;
        dialogRef.afterClosed().subscribe(() => {
          resolve(loginObject);
        });
      } else {
        resolve(loginObject);
      }
    });
  }
}
